import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { formatINR, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Layers, Send, Download } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePagination } from "@/components/Pagination";

const HEADS = [
  "Retention PO Terms", "IR Retention", "Safety Retention",
  "BG Retention", "GST Retention", "Other Retention",
];

export default function Retentions() {
  const [holds, setHolds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [head, setHead] = useState("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [client, setClient] = useState("all");
  const [search, setSearch] = useState("");
  const [clientOptions, setClientOptions] = useState([]);

  // Export dialog state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportHead, setExportHead] = useState("all");
  const [exportClient, setExportClient] = useState("all");
  const [exportStatus, setExportStatus] = useState("all");
  const [exportType, setExportType] = useState("all");
  const [exportCount, setExportCount] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const load = async () => {
    const params = {};
    if (head !== "all") params.head = head;
    if (status !== "all") params.status = status;
    const [h, r] = await Promise.all([
      api.get("/retentions", { params }),
      api.get("/retention-requests", { params }),
    ]);
    setHolds(h.data);
    setRequests(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [head, status]);

  useEffect(() => {
    api.get("/invoices/filters").then(({ data }) => {
      setClientOptions(data.party_client_name || []);
    }).catch(() => {});
  }, []);

  // Merge & enrich with type
  const rows = useMemo(() => {
    // If a release request exists for (invoice_id, head), suppress the underlying "hold" rows
    // for the same (invoice_id, head) — the raised request now represents that head's lifecycle.
    const raisedKey = new Set(requests.map((r) => `${r.invoice_id}::${r.head}`));
    const filteredHolds = holds.filter((r) => !raisedKey.has(`${r.invoice_id}::${r.head}`));
    const deriveHoldType = (r) => r.status === "Paid"
      ? "Retention Paid Without Request"
      : "Yet to Raise Retention Request";
    const deriveReqType = (r) => r.status === "Paid"
      ? "Retention Paid On Request"
      : "Retention Request Raised";
    const all = [
      ...filteredHolds.map((r) => ({ ...r, _type: deriveHoldType(r), _key: `H-${r.id}` })),
      ...requests.map((r) => ({ ...r, _type: deriveReqType(r), _key: `R-${r.id}` })),
    ];
    const filteredByType = type === "all" ? all : all.filter((r) => r._type === type);
    const filteredByClient = client === "all" ? filteredByType : filteredByType.filter((r) => (r.party_client_name || "") === client);
    const q = search.trim().toLowerCase();
    const filtered = !q ? filteredByClient : filteredByClient.filter((r) =>
      (r.invoice_number || "").toLowerCase().includes(q) ||
      (r.party_client_name || "").toLowerCase().includes(q) ||
      (r.request_number || "").toLowerCase().includes(q) ||
      (r.invoice_description || "").toLowerCase().includes(q)
    );
    filtered.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return filtered;
  }, [holds, requests, type, client, search]);

  const {
    pageItems: pagedRows,
    page: rPage, setPage: setRPage,
    pageSize: rPageSize, setPageSize: setRPageSize,
    totalPages: rTotalPages, totalItems: rTotalItems,
  } = usePagination(rows, "voltledger.retentions.pageSize");

  const counts = useMemo(() => {
    const raisedKey = new Set(requests.map((r) => `${r.invoice_id}::${r.head}`));
    const filteredHolds = holds.filter((r) => !raisedKey.has(`${r.invoice_id}::${r.head}`));
    return {
      pendingHolds: filteredHolds.filter((r) => r.status !== "Paid").length,
      paidWithoutReq: filteredHolds.filter((r) => r.status === "Paid").length,
      pendingReqs: requests.filter((r) => r.status !== "Paid").length,
      paidOnReq: requests.filter((r) => r.status === "Paid").length,
      total: rows.length,
    };
  }, [holds, requests, rows.length]);

  // Preview count when export dialog open / selection changes
  useEffect(() => {
    if (!exportOpen) return;
    const params = {};
    if (exportHead && exportHead !== "all") params.head = exportHead;
    if (exportClient && exportClient !== "all") params.party_client_name = exportClient;
    if (exportStatus && exportStatus !== "all") params.status = exportStatus;
    if (exportType && exportType !== "all") params.row_type = exportType;
    setExportCount(null);
    api.get("/exports/retentions/count", { params })
      .then(({ data }) => setExportCount(data))
      .catch(() => setExportCount({ count: 0, max: 1000 }));
  }, [exportOpen, exportHead, exportClient, exportStatus, exportType]);

  const exportExcel = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (exportHead && exportHead !== "all") params.head = exportHead;
      if (exportClient && exportClient !== "all") params.party_client_name = exportClient;
      if (exportStatus && exportStatus !== "all") params.status = exportStatus;
      if (exportType && exportType !== "all") params.row_type = exportType;
      const res = await api.get("/exports/retentions", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const hSuf = (exportHead && exportHead !== "all") ? exportHead.replace(/\s+/g, "_") : "AllHeads";
      const cSuf = (exportClient && exportClient !== "all") ? "_" + exportClient.replace(/\s+/g, "_") : "";
      const sSuf = (exportStatus && exportStatus !== "all") ? "_" + exportStatus : "";
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `retentions_${hSuf}${cSuf}${sSuf}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      setExportOpen(false);
    } catch (e) {
      let msg = "Export failed";
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch (_) {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1600px]">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Operations</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Retention Requests</h1>
          <p className="text-sm text-slate-500 mt-2">
            All retention entries across invoices · {counts.pendingHolds} yet to raise · {counts.pendingReqs} raised · {counts.paidOnReq} paid on request · {counts.paidWithoutReq} paid without request
          </p>
        </div>
        <Dialog open={exportOpen} onOpenChange={setExportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-none h-10 border-slate-300" data-testid="ret-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export Excel
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Export Retentions</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Type</Label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger data-testid="ret-export-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Retention Request Raised">Retention Request Raised</SelectItem>
                    <SelectItem value="Yet to Raise Retention Request">Yet to Raise Retention Request</SelectItem>
                    <SelectItem value="Retention Paid On Request">Retention Paid On Request</SelectItem>
                    <SelectItem value="Retention Paid Without Request">Retention Paid Without Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Head</Label>
                <Select value={exportHead} onValueChange={setExportHead}>
                  <SelectTrigger data-testid="ret-export-head" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Heads</SelectItem>
                    {HEADS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Client</Label>
                <Select value={exportClient} onValueChange={setExportClient}>
                  <SelectTrigger data-testid="ret-export-client" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Status</Label>
                <Select value={exportStatus} onValueChange={setExportStatus}>
                  <SelectTrigger data-testid="ret-export-status" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {exportCount !== null && (
                <div
                  className={`text-xs px-3 py-2 border ${
                    exportCount.count > exportCount.max
                      ? "bg-red-50 border-red-200 text-red-800"
                      : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}
                  data-testid="ret-export-count-info"
                >
                  {exportCount.count === 0
                    ? "No retention entries match these filters."
                    : exportCount.count > exportCount.max
                    ? exportHead === "all"
                      ? `${exportCount.count} rows match. Maximum allowed per download is ${exportCount.max}. Please choose a specific Head to narrow the export.`
                      : `${exportCount.count} rows match. Maximum allowed per download is ${exportCount.max}. Please add a Client filter to narrow the export.`
                    : `${exportCount.count} of max ${exportCount.max} rows will be exported.`}
                </div>
              )}
              <div className="text-[10px] text-slate-400">
                Columns: SN, Type, Invoice #, Client, Head, Req. No, Req. Date, Amount, Paid, Status. Maximum 1000 rows per download.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExportOpen(false)} className="rounded-none">Cancel</Button>
              <Button
                onClick={exportExcel}
                disabled={exportLoading || !exportCount || exportCount.count === 0 || exportCount.count > exportCount.max}
                className="bg-[#002FA7] hover:bg-[#00227A] rounded-none"
                data-testid="ret-export-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                {exportLoading ? "Downloading…" : "Download"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Invoice #, client, request no…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none pl-9"
              data-testid="ret-search"
            />
          </div>
        </div>
        <div className="w-56">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Type</div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-none" data-testid="ret-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Retention Request Raised">Retention Request Raised</SelectItem>
              <SelectItem value="Yet to Raise Retention Request">Yet to Raise Retention Request</SelectItem>
              <SelectItem value="Retention Paid On Request">Retention Paid On Request</SelectItem>
              <SelectItem value="Retention Paid Without Request">Retention Paid Without Request</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Client</div>
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger className="rounded-none" data-testid="ret-filter-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Head</div>
          <Select value={head} onValueChange={setHead}>
            <SelectTrigger className="rounded-none" data-testid="ret-filter-head"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Heads</SelectItem>
              {HEADS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none" data-testid="ret-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-[140px]">Type</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 min-w-[200px]">Description</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Client</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Head</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Req. No</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Req. Date</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Paid</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-sm text-slate-400 py-12">
                No retention entries found.
              </td></tr>
            )}
            {pagedRows.map((r) => {
              const t = r._type;
              const toneByType = {
                "Retention Request Raised": "bg-emerald-50 text-emerald-700 border-emerald-200",
                "Yet to Raise Retention Request": "bg-red-50 text-red-700 border-red-200",
                "Retention Paid On Request": "bg-indigo-100 text-indigo-800 border-indigo-300",
                "Retention Paid Without Request": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
              };
              const isPaidType = t === "Retention Paid On Request" || t === "Retention Paid Without Request";
              const isRequest = t === "Retention Request Raised" || t === "Retention Paid On Request";
              return (
                <tr key={r._key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ret-row-${r.id}`}>
                  <td className="px-3 py-3 w-[140px]">
                    <span className={`inline-flex items-start gap-1 px-2 py-1 border text-[10px] font-semibold leading-tight whitespace-normal ${toneByType[t] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                      {isPaidType ? <Send className="w-3 h-3 mt-0.5 shrink-0" /> : (isRequest ? <Send className="w-3 h-3 mt-0.5 shrink-0" /> : <Layers className="w-3 h-3 mt-0.5 shrink-0" />)}
                      <span className="break-words">{t}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/invoices/${r.invoice_id}`} state={{ from: "/retentions", fromLabel: "Back to Retentions" }} className="text-slate-900 font-medium hover:text-[#002FA7]">
                      {r.invoice_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs min-w-[200px] max-w-[320px]" title={r.invoice_description || ""}>
                    {r.invoice_description ? (
                      <span className="block leading-snug whitespace-normal break-words">{r.invoice_description}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.party_client_name || "—"}</td>
                  <td className="px-4 py-3">{r.head}</td>
                  <td className="px-4 py-3 font-mono-data">{r.request_number || "—"}</td>
                  <td className="px-4 py-3">{formatDate(r.request_date)}</td>
                  <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.amount)}</td>
                  <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.paid_amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`status-pill ${r.status === "Paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={rPage} setPage={setRPage}
          pageSize={rPageSize} setPageSize={setRPageSize}
          totalPages={rTotalPages} totalItems={rTotalItems}
          testId="retentions-pagination"
        />
      </div>
    </div>
  );
}
